import axios from 'axios'

import { API_CHECK_AUTH_URL, API_CONTROL_REQUESTS, API_TOKEN_REFRESH } from '../../../service/api.endpoint'
import {storeAuthenticatedUser} from '../../../service/authUser'

const api = axios.create({
  baseURL: API_CONTROL_REQUESTS.replace(/requests\/?$/, ''),
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

let isRefreshing = false
let refreshQueue: Array<(token: string | null) => void> = []

function resolveRefreshQueue(token: string | null) {
  refreshQueue.forEach((callback) => callback(token))
  refreshQueue = []
}

async function refreshAccessToken() {
  const refresh = localStorage.getItem('refreshToken')

  if (!refresh) {
    throw new Error('Refresh token is missing')
  }

  const response = await axios.post(API_TOKEN_REFRESH, { refresh }, {
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
    },
  })

  const { access, refresh: nextRefresh } = response.data
  localStorage.setItem('accessToken', access)
  if (nextRefresh) {
    localStorage.setItem('refreshToken', nextRefresh)
  }

  const userInfoResponse = await axios.get(API_CHECK_AUTH_URL, {
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
      Authorization: `Bearer ${access}`,
    },
  })

  if (userInfoResponse.status === 200) {
    storeAuthenticatedUser(userInfoResponse.data.data)
  }

  return access
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push((token) => {
          if (!token) {
            reject(error)
            return
          }

          originalRequest.headers.Authorization = `Bearer ${token}`
          resolve(api(originalRequest))
        })
      })
    }

    isRefreshing = true

    try {
      const token = await refreshAccessToken()
      resolveRefreshQueue(token)
      originalRequest.headers.Authorization = `Bearer ${token}`
      return api(originalRequest)
    } catch (refreshError) {
      resolveRefreshQueue(null)
      localStorage.setItem('accessToken', '')
      localStorage.setItem('refreshToken', '')
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

export default api
